//askSmart-ui/src/pages/AdminSMRTDocsView.tsx
import React from "react";
import "./ManageSMRTDocs.css";
import { useState, useEffect, useRef, useCallback } from "react";
import SMRTDocsFileHistory from "../components/SMRTDocsFileHistory";
import SMRTDocsNotifyModal from "../components/SMRTDocsNotifyModal";
import SMRTDocsSignersModal from "../components/SMRTDocsSignersModal";
import SMRTDocsSigningView from "../components/SMRTDocsSigningView";
import SMRTDocsFieldPlacementModal, {
  SMRTDocsPlacedField,
} from "../components/SMRTDocsFieldPlacementModal";
import SMRTDocsEditor from "./SMRTDocsEditor";
import { buildSignedPdf, sha256OfString } from "../utils/smrtdocsPdf";
import {
  fetchSmrtDocsFileUrl,
  openSmrtDocsFile,
  downloadBlobUrl,
  smrtDocsFileDownloadUrl,
  smrtDocsCertificateUrl,
} from "../utils/smrtdocsFile";
import { DownloadIcon } from "../components/DownloadIcon";
import {
  getNotifications,
  saveNotifications,
  getServiceLocalDocs,
} from "../components/SMRTDocsTrafficModal";
import { useCustomization } from "../context/CustomizationContext";
import { useAuth } from "../hooks/useAuth";
import { isMobileApp } from "../utils/isMobileApp";

// SHA256 of base64-decoded bytes — used to fingerprint the document at
// send time so every signer's audit record references the exact same
// content. Runs entirely in-browser via SubtleCrypto; no library added.
async function sha256OfBase64(base64: string): Promise<string> {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

export default function AdminSMRTDocsView({
  template,
  services: _services,
  assigned: _assigned,
  viewServiceIndex,
  setViewServiceIndex: _setViewServiceIndex,
  isUserView = false,
  userId,
  userEmail,
  userName,
}: any) {
  const { customization } = useCustomization();

  // Documents are stored per service, under the service's permanent id rather
  // than its position — positions shift whenever an earlier service is
  // deleted. Everything this pane reads and writes is scoped to this one id,
  // which is what stops a duplicated service showing the original's documents.
  const serviceId: string | undefined =
    _services?.[viewServiceIndex]?.id ?? undefined;
  const { user: authUser, org, roles } = useAuth();
  const adminName = authUser?.name || authUser?.email || "Admin";
  // Gate for revealing encrypted document fields. Mirrors the Profiles
  // sensitive-reveal gate; the matching backend endpoint enforces it too.
  const canRevealEncrypted =
    Array.isArray(roles?.permissions) &&
    roles.permissions.includes("smrtdocs.sensitive");
  // Reveal-encrypted-fields modal. Replaces the browser prompt/alert with a
  // styled, platform-consistent dialog that captures the (required) access
  // reason and surfaces errors inline.
  const [unmaskModal, setUnmaskModal] = useState<{ field: any } | null>(null);
  const [unmaskReason, setUnmaskReason] = useState("");
  const [unmaskError, setUnmaskError] = useState<string | null>(null);
  const [unmaskBusy, setUnmaskBusy] = useState(false);
  const appName = customization?.branding?.title || "AskSmart";

  const [files, setFiles] = useState<Record<string, any>>({});
  const [issued, setIssued] = useState<Record<string, any>>({});
  const [history, setHistory] = useState<Record<string, any[]>>({});
  const [reminders, setReminders] = useState<Record<string, any>>({});
  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [approved, setApproved] = useState<Record<string, boolean>>({});
  const [notified, setNotified] = useState<Record<string, boolean>>({});
  const [localDocs, setLocalDocs] = useState<any>({
    attachedFields: {},
    steps: [],
  });
  // Per-field placement coordinates set by the admin via the field
  // placement modal. Keyed by field.key, values are arrays of placed
  // fields (signature/initial/date/title/text) with normalized x/y and
  // a signerIndex.
  const [placements, setPlacements] = useState<
    Record<string, SMRTDocsPlacedField[]>
  >({});
  // Append-only audit log keyed by field.key. One entry per signer per
  // document (backend dedupes by userId + signingOrderPosition and
  // stamps ipAddress from the request).
  const [audit, setAudit] = useState<
    Record<
      string,
      {
        userId: string;
        orgId: string;
        documentId: string;
        documentVersion: string;
        documentHash: string;
        signingOrderPosition: number;
        signedAt: string;
        ipAddress: string;
        userAgent: string;
        consentText: string;
        signatureType: string;
        verificationCode: string;
      }[]
    >
  >({});
  const [localEditOpen, setLocalEditOpen] = useState(false);

  // Local edits are scoped to the currently-viewed service so an addition made
  // on one of the user's assigned services never shows up on another. The slice
  // for viewServiceIndex is read/written under localDocs.byService[index].
  const serviceLocalDocs = getServiceLocalDocs(localDocs, viewServiceIndex);
  const setServiceLocalDocs = (nextSlice: any) => {
    const key = String(viewServiceIndex ?? "");
    setLocalDocs((prev: any) => ({
      ...prev,
      byService: { ...(prev?.byService || {}), [key]: nextSlice },
    }));
  };

  const userStateLoaded = useRef(false);
  const overridesLoaded = useRef(false);

  // Fetch user state from backend on mount / userId change (with polling)
  const lastUserStateHash = useRef("");
  useEffect(() => {
    userStateLoaded.current = false;
    lastUserStateHash.current = "";
    if (!userId) return;
    // No service, nothing to load — asking without one returns every service
    // merged, which is exactly the leak this pane must not have.
    if (!serviceId) return;
    const fetchState = () => {
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(userId)}?serviceId=${encodeURIComponent(serviceId)}`,
        { credentials: "include" },
      )
        .then((r) => r.json())
        .then((data) => {
          const stateOnly = {
            files: data.files || {},
            issued: data.issued || {},
            history: data.history || {},
            reminders: data.reminders || {},
            approved: data.approved || {},
            notified: data.notified || {},
            localDocs: data.localDocs || { attachedFields: {}, steps: [] },
            placements: data.placements || {},
            audit: data.audit || {},
          };
          const hash = JSON.stringify(stateOnly);
          if (hash !== lastUserStateHash.current) {
            setFiles(stateOnly.files);
            setIssued(stateOnly.issued);
            setHistory(stateOnly.history);
            setReminders(stateOnly.reminders);
            setApproved(stateOnly.approved);
            setNotified(stateOnly.notified);
            setLocalDocs(stateOnly.localDocs);
            setPlacements(stateOnly.placements);
            setAudit(stateOnly.audit);
            lastUserStateHash.current = hash;
          }
          userStateLoaded.current = true;
        })
        .catch((err) => console.error("Failed to load user state:", err));
    };
    fetchState();
    let interval = setInterval(fetchState, 12000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        fetchState();
        interval = setInterval(fetchState, 12000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // serviceId included: switching service tabs must reload that service's
    // documents, not keep showing the previous one's.
  }, [userId, serviceId]);

  // Fetch overrides from backend on mount
  useEffect(() => {
    overridesLoaded.current = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/overrides`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        setOverrides(data.overrides || {});
        overridesLoaded.current = true;
      })
      .catch((err) => console.error("Failed to load overrides:", err));
  }, []);

  // Debounced PUT for user state
  const userStateSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveUserState = useCallback(() => {
    if (!userStateLoaded.current || !userId) return;
    // A save with no service would have nowhere to go — the server rejects it
    // rather than guessing, so don't send one.
    if (!serviceId) return;
    if (userStateSaveTimer.current) clearTimeout(userStateSaveTimer.current);
    userStateSaveTimer.current = setTimeout(() => {
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(userId)}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            files,
            issued,
            approved,
            notified,
            history,
            reminders,
            localDocs,
            placements,
            audit,
            // Which service these documents belong to.
            serviceId,
            // Service context for collaborator write enforcement (ignored for
            // owner/admin saves). Still an index: collaborator grants are
            // recorded positionally and haven't moved onto ids yet.
            serviceIndex: viewServiceIndex,
          }),
        },
      ).catch((err) => console.error("Failed to save user state:", err));
    }, 1000);
  }, [
    userId,
    serviceId,
    viewServiceIndex,
    files,
    issued,
    approved,
    notified,
    history,
    reminders,
    localDocs,
    placements,
    audit,
  ]);

  useEffect(() => {
    saveUserState();
  }, [saveUserState]);

  // Debounced PUT for overrides
  const overridesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!overridesLoaded.current) return;
    if (overridesSaveTimer.current) clearTimeout(overridesSaveTimer.current);
    overridesSaveTimer.current = setTimeout(() => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtdocs/overrides`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
      }).catch((err) => console.error("Failed to save overrides:", err));
    }, 1000);
  }, [overrides]);

  // iOS WKWebView (Capacitor) blocks the `window.open()` popup the desktop
  // flow uses, so on mobile we render the file in an in-app modal instead.
  const mobileMode =
    isMobileApp() ||
    window.navigator.userAgent.includes("iPhone") ||
    window.navigator.userAgent.includes("iPad");

  const [mobileViewFile, setMobileViewFile] = useState<{
    src: string;
    name: string;
    contentType: string;
  } | null>(null);

  const [fileError, setFileError] = useState<string | null>(null);

  // Open file via the backend proxy, which returns a blob: object URL so the
  // browser streams the PDF into its native viewer (print/download toolbar
  // included). The old base64 data-URI path went blank on larger docs, and the
  // storage firewall blocks direct SAS reads — see utils/smrtdocsFile.ts.
  //
  // Desktop opens go through openSmrtDocsFile(), which creates the tab
  // synchronously so the browser does not drop it as an unattended popup.
  // Failures must reach the user: a swallowed error here is what made a broken
  // document indistinguishable from a dead button.
  const openFile = async (fileObj: any) => {
    if (!fileObj) return;
    setFileError(null);
    if (fileObj.blobName) {
      try {
        if (mobileMode) {
          // The native app's WKWebView has no tabs, so keep the fetch + blob:
          // path here and render into the in-app viewer.
          const url = await fetchSmrtDocsFileUrl(userId, fileObj.blobName);
          setMobileViewFile({
            src: url,
            name: fileObj.name || "Document",
            contentType: fileObj.type || "application/pdf",
          });
          return;
        }
        const { opened, url } = openSmrtDocsFile(userId, fileObj.blobName);
        // Popup blocked (managed/corporate policy). Navigate this tab instead —
        // a top-level navigation has no outer page, so the backend's global
        // X-Frame-Options: DENY never applies; Back returns to this screen.
        // Never fall back to the in-page <iframe> here: this URL is
        // cross-origin (app → saas) and XFO makes the browser discard it — a
        // silent blank modal for exactly the users who hit the popup block.
        if (!opened) window.location.href = url;
      } catch (err) {
        console.error("Failed to open file:", err);
        setFileError(
          (err as Error).message ||
            "This document could not be opened. Please try again.",
        );
      }
    } else if (fileObj.data) {
      if (mobileMode) {
        setMobileViewFile({
          src: fileObj.data,
          name: fileObj.name || "Document",
          contentType: fileObj.type || "",
        });
        return;
      }
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(
          `<iframe src="${fileObj.data}" width="100%" height="100%"></iframe>`,
        );
      }
    }
  };

  const [signModal, setSignModal] = useState<{
    fieldKey: string;
    countersignRequired: boolean;
    // When true the modal is part of the Edit Fields flow — on submit
    // we buffer the picked signers and chain into the placement modal
    // instead of committing to `issued` immediately.
    openPlacementAfter?: boolean;
  } | null>(null);

  // Authorized, audited download of a completed document with its encrypted
  // field values revealed in clear text. The stored signed PDF always masks
  // these values; this rebuilds a one-off copy from the original document +
  // server-decrypted plaintext. Every decrypt is logged server-side against
  // the supplied reason. Gated by `smrtdocs.sensitive` (enforced on the API).
  // Opens the styled reveal modal for a document. The actual decrypt +
  // download runs from runUnmaskDownload once the admin supplies a reason.
  const openUnmaskModal = (field: any) => {
    setUnmaskReason("");
    setUnmaskError(null);
    setUnmaskBusy(false);
    setUnmaskModal({ field });
  };

  const runUnmaskDownload = async () => {
    if (!unmaskModal) return;
    const field = unmaskModal.field;
    const key = field.key;
    const fieldPlacements = placements[key] || [];
    const encPlacements = fieldPlacements.filter(
      (p: any) => p.type === "text-encrypted",
    );
    if (encPlacements.length === 0) {
      setUnmaskError("This document has no encrypted fields.");
      return;
    }
    const reason = unmaskReason.trim();
    if (!reason) {
      setUnmaskError("A reason is required to reveal encrypted fields.");
      return;
    }

    setUnmaskBusy(true);
    setUnmaskError(null);
    try {
      // 1. Authorized + audited decrypt of every encrypted field on the doc.
      const revRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/user-state/${encodeURIComponent(userId)}/reveal`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ issuedKey: key, reason }),
        },
      );
      if (!revRes.ok) {
        const body = await revRes.json().catch(() => ({}));
        setUnmaskError(
          body?.error ||
            `Failed to reveal encrypted fields (${revRes.status})`,
        );
        setUnmaskBusy(false);
        return;
      }
      const { values } = await revRes.json();

      // 2. Fetch the original (pre-signature) document so we can re-stamp it
      //    with the revealed values rather than the masked signed blob.
      const it = issued[key] || {};
      const origBlobName = it.originalFile?.blobName || files[key]?.blobName;
      if (!origBlobName) {
        setUnmaskError("Original document could not be located.");
        setUnmaskBusy(false);
        return;
      }
      const baseRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/_?blobName=${encodeURIComponent(origBlobName)}&format=base64`,
        { credentials: "include" },
      );
      if (!baseRes.ok) {
        setUnmaskError("Failed to load the original document.");
        setUnmaskBusy(false);
        return;
      }
      const { base64 } = await baseRes.json();
      const bin = atob(base64);
      const origBytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) origBytes[i] = bin.charCodeAt(i);

      // 3. Clone signers and substitute decrypted plaintext into the
      //    encrypted placements so buildSignedPdf stamps the real values.
      const signers = (Array.isArray(it.signers) ? it.signers : []).map(
        (s: any) => ({ ...s, fieldValues: { ...(s.fieldValues || {}) } }),
      );
      for (const p of encPlacements) {
        const val = values?.[p.id];
        if (val == null) continue;
        const si = typeof p.signerIndex === "number" ? p.signerIndex : 0;
        if (signers[si]) signers[si].fieldValues[p.id] = val;
      }

      const bytes = await buildSignedPdf({
        originalBytes: origBytes,
        placements: fieldPlacements,
        signers,
        revealEncrypted: true,
      });
      const blob = new Blob([new Uint8Array(bytes)], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        (field.label || key || "document").replace(/\s+/g, "_") +
        "_unmasked.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      setUnmaskBusy(false);
      setUnmaskModal(null);
      setUnmaskReason("");
    } catch (err) {
      console.error("Failed to download unmasked document:", err);
      setUnmaskError("Failed to download the unmasked document.");
      setUnmaskBusy(false);
    }
  };

  // Buffer between the signers modal and the placement modal so a
  // doc isn't flipped to "Client Received" until placements are saved.
  const [pendingSelection, setPendingSelection] = useState<{
    fieldKey: string;
    signers: any[];
    documentHash: string;
  } | null>(null);

  // Internal signing flow — when set, renders the acknowledge → sign modal
  // for the current signer of the given field.
  const [signingFlow, setSigningFlow] = useState<{
    fieldKey: string;
    documentName: string;
  } | null>(null);

  // Field placement modal — admin opens this on an uploaded template doc
  // to drop Signature / Initial / Date / Title / Text fields per signer.
  const [placementModal, setPlacementModal] = useState<{
    fieldKey: string;
    documentName: string;
    blobName: string;
    countersignRequired: boolean;
  } | null>(null);

  const [historyOpen, setHistoryOpen] = useState<string | null>(null);
  const [notifyOpen, setNotifyOpen] = useState<string | null>(null);
  const [notifyConfirmOpen, setNotifyConfirmOpen] = useState(false);
  const [localTypeError, setLocalTypeError] = useState(false);

  return (
    <>
      {/* Service selection is handled by the page-level tabs in both the user
          (SMRTDocs) and admin (AdminSMRTDocsViewWrapper) views, so no in-panel
          service dropdown here. */}
      {!isUserView && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <button className="edit-icon" onClick={() => setLocalEditOpen(true)}>
            ✏️ Local Edit
          </button>
        </div>
      )}
      <div className="template-list smrtdocs-editor smrtdocs-scope">
        {template?.steps?.length ? (
          [...template.steps, ...(serviceLocalDocs.steps || [])].map((step: any) => {
            const isLocalStep = String(step.id).startsWith("local_");
            const attachedLocalFields = !isLocalStep
              ? serviceLocalDocs.attachedFields?.[step.id] || []
              : [];
            const allFields = isLocalStep
              ? step.fields
              : [...step.fields, ...attachedLocalFields];
            return (
              <div
                key={step.id}
                className="template-card"
                style={{ marginBottom: "16px" }}
              >
                <div className="template-card-header">
                  <input className="manage-input" value={step.label} disabled />
                </div>

                <div className="template-grid-header">
                  <div>Document</div>
                  <div>Status Requirements</div>
                  <div>Status</div>
                  <div>File</div>
                  <div>Actions</div>
                </div>

                {allFields
                  .filter((field: any) => {
                    const overrideKey = `${userId}_${field.key}`;
                    const override = overrides[overrideKey];

                    if (isUserView && override?.hidden) return false;

                    return true;
                  })
                  .map((field: any) => {
                    const overrideKey = `${userId}_${field.key}`;
                    const override = overrides[overrideKey] || {};

                    const effectiveField = {
                      ...field,
                      ...override,
                    };

                    console.log(
                      `[SMRT Docs] isUserView=${isUserView} | field=${field.key} | template.approvalRequired=${field.approvalRequired} | override.approvalRequired=${override.approvalRequired} | effective.approvalRequired=${effectiveField.approvalRequired} | userId=${userId} | overrideKey=${overrideKey}`,
                    );

                    return (
                      <div key={field.key} className="template-field-row">
                        {/* A hidden document is greyed and struck through, not
                            merely flagged — it reads as withdrawn at a glance.
                            Dimmed on the cell rather than the row: opacity on a
                            parent composites its children with it, and that
                            would take the H button down too, which is the only
                            way back. */}
                        <input
                          className="manage-input smrtdocs-document-cell"
                          value={effectiveField.label}
                          disabled
                          style={{
                            textDecoration: effectiveField.hidden
                              ? "line-through"
                              : "none",
                            opacity: effectiveField.hidden ? 0.5 : 1,
                          }}
                        />

                        <div className="template-field-actions">
                          {field.type === "template" && (
                            <div
                              title="Template Document Type"
                              className="btn-toggle smrtdocs-doc-type"
                            >
                              T
                            </div>
                          )}

                          {field.type === "certificate" && (
                            <div
                              title="Certificate Document Type"
                              className="btn-toggle smrtdocs-doc-type"
                            >
                              C
                            </div>
                          )}

                          {field.type === "info" && (
                            <div
                              title="Info Document Type"
                              className="btn-toggle smrtdocs-doc-type"
                            >
                              I
                            </div>
                          )}
                          {field.mandatory && (
                            <div
                              title="Mandatory"
                              className="btn-toggle btn-toggle-mandatory-on"
                            >
                              M
                            </div>
                          )}
                          {field.type !== "info" && (
                            <div
                              title="Approval Required"
                              className={`btn-toggle ${
                                effectiveField.approvalRequired
                                  ? "btn-toggle-approval-on"
                                  : "btn-toggle-off"
                              }`}
                              onClick={() => {
                                const key = `${userId}_${field.key}`;
                                setOverrides({
                                  ...overrides,
                                  [key]: {
                                    ...overrides[key],
                                    approvalRequired:
                                      !effectiveField.approvalRequired,
                                  },
                                });
                              }}
                            >
                              ✔
                            </div>
                          )}
                          {field.type === "template" && (
                            <div
                              title="Countersign Required"
                              className={`btn-toggle ${
                                effectiveField.countersignRequired
                                  ? "btn-toggle-countersign-on"
                                  : "btn-toggle-off"
                              }`}
                              onClick={() => {
                                const key = `${userId}_${field.key}`;
                                setOverrides({
                                  ...overrides,
                                  [key]: {
                                    ...overrides[key],
                                    countersignRequired:
                                      !effectiveField.countersignRequired,
                                  },
                                });
                              }}
                            >
                              ✎
                            </div>
                          )}
                          <div
                            title="Hide Document"
                            className={`btn-toggle ${
                              effectiveField.hidden
                                ? "btn-toggle-delete"
                                : "btn-toggle-off"
                            }`}
                            onClick={() => {
                              const key = `${userId}_${field.key}`;
                              setOverrides({
                                ...overrides,
                                [key]: {
                                  ...overrides[key],
                                  hidden: !effectiveField.hidden,
                                },
                              });
                            }}
                          >
                            H
                          </div>
                        </div>
                        <div className="manage-input smrtdocs-status-cell">
                          {field.type === "certificate"
                            ? !files[field.key]
                              ? "Pending"
                              : !issued[field.key]
                                ? "Draft"
                                : effectiveField.approvalRequired
                                  ? approved[field.key]
                                    ? "Active"
                                    : "Client Submitted"
                                  : "Active"
                            : field.type === "template"
                              ? !files[field.key]
                                ? "Pending"
                                : !issued[field.key]
                                  ? "Draft"
                                  : typeof issued[field.key] === "object"
                                    ? issued[field.key].status === "Active"
                                      ? "Active"
                                      : issued[field.key].status ===
                                          "Client Submitted"
                                        ? effectiveField.approvalRequired
                                          ? "Client Submitted"
                                          : "Active"
                                        : "Client Received"
                                    : "Client Received"
                              : field.type === "info"
                                ? issued[field.key]
                                  ? "Active"
                                  : files[field.key]
                                    ? "Draft"
                                    : "Pending"
                                : issued[field.key]
                                  ? "Client Received"
                                  : files[field.key]
                                    ? "Pending"
                                    : "Draft"}
                        </div>
                        <div className="smrtdocs-upload-box">
                          {field.type === "template" ? (
                            isUserView ? (
                              <div className="smrtdocs-upload-inner">
                                {!files[field.key] ? (
                                  "Pending"
                                ) : issued[field.key]?.status !== "Active" ? (
                                  (() => {
                                    // Sign action lives in the Actions column
                                    // now — this slot only shows the order-
                                    // aware status label.
                                    const it = issued[field.key];
                                    const signers = Array.isArray(it?.signers)
                                      ? it.signers
                                      : [];
                                    const idx =
                                      typeof it?.currentSignerIndex === "number"
                                        ? it.currentSignerIndex
                                        : 0;
                                    const currentSigner = signers[idx];
                                    const isCurrent =
                                      currentSigner &&
                                      currentSigner.userId ===
                                        (authUser as any)?.userID;
                                    const alreadySigned =
                                      signers.some(
                                        (s: any) =>
                                          s.userId ===
                                            (authUser as any)?.userID &&
                                          s.signedAt,
                                      );
                                    if (isCurrent && !alreadySigned) {
                                      return "Awaiting your signature";
                                    }
                                    if (alreadySigned) {
                                      return "Awaiting other signers";
                                    }
                                    return "Pending signatures";
                                  })()
                                ) : (
                                  <span
                                    className="smrtdocs-file-link"
                                    onClick={() => openFile(files[field.key])}
                                  >
                                    {files[field.key].name}
                                  </span>
                                )}
                              </div>
                            ) : files[field.key] ? (
                              <div className="smrtdocs-upload-inner smrtdocs-upload-file">
                                <span
                                  className="smrtdocs-file-link"
                                  onClick={() => openFile(files[field.key])}
                                >
                                  {files[field.key].name}
                                </span>

                                <button
                                  className="btn-icon smrtdocs-btn-multi"
                                  title="Reload Document"
                                  onClick={() => {
                                    const updated = { ...files };
                                    delete updated[field.key];
                                    setFiles(updated);

                                    const updatedIssued = { ...issued };
                                    delete updatedIssued[field.key];
                                    setIssued(updatedIssued);

                                    // Clear any placed fields tied to the
                                    // old document — editing a new upload
                                    // must not inherit positions set against
                                    // the previous PDF.
                                    const updatedPlacements = { ...placements };
                                    delete updatedPlacements[field.key];
                                    setPlacements(updatedPlacements);

                                    // Clear read state so item reappears in GREEN after re-upload
                                    const nid = `${userId}_${field.key}`;
                                    saveNotifications(
                                      getNotifications().filter(
                                        (n) => n.id !== nid,
                                      ),
                                    );
                                  }}
                                >
                                  R
                                </button>
                              </div>
                            ) : (
                              <label className="smrtdocs-upload-inner">
                                Upload
                                <input
                                  type="file"
                                  style={{ display: "none" }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    const formData = new FormData();
                                    formData.append("file", file);

                                    try {
                                      const res = await fetch(
                                        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/${encodeURIComponent(field.key)}?serviceIndex=${viewServiceIndex}`,
                                        {
                                          method: "POST",
                                          credentials: "include",
                                          body: formData,
                                        },
                                      );
                                      if (!res.ok)
                                        throw new Error("Upload failed");
                                      const ref = await res.json();

                                      setFiles({
                                        ...files,
                                        [field.key]: {
                                          name: ref.name,
                                          type: ref.type,
                                          blobName: ref.blobName,
                                          uploadedAt: ref.uploadedAt,
                                        },
                                      });

                                      setIssued({
                                        ...issued,
                                        [field.key]: false,
                                      });
                                    } catch (err) {
                                      console.error("File upload failed:", err);
                                    }
                                  }}
                                />
                              </label>
                            )
                          ) : !isUserView &&
                            field.type === "certificate" &&
                            !issued[field.key] ? (
                            <div className="smrtdocs-upload-inner">Pending</div>
                          ) : (!isUserView && files[field.key]) ||
                            (isUserView &&
                              field.type === "info" &&
                              issued[field.key] &&
                              files[field.key]) ||
                            (isUserView &&
                              field.type === "certificate" &&
                              issued[field.key] &&
                              files[field.key]) ? (
                            <div className="smrtdocs-upload-inner smrtdocs-upload-file">
                              <span
                                className="smrtdocs-file-link"
                                onClick={() => openFile(files[field.key])}
                              >
                                {files[field.key].name}
                              </span>

                              {!(isUserView && field.type === "info") &&
                                !(isUserView && approved[field.key]) &&
                                !(
                                  isUserView &&
                                  field.type === "certificate" &&
                                  !effectiveField.approvalRequired &&
                                  issued[field.key]
                                ) && (
                                  <button
                                    className="btn-icon smrtdocs-btn-multi"
                                    title="Reload Document"
                                    onClick={() => {
                                      if (files[field.key]) {
                                        const existingHistory =
                                          history[field.key] || [];

                                        const alreadyExists =
                                          existingHistory.some(
                                            (h: any) =>
                                              h.file?.name ===
                                              files[field.key]?.name,
                                          );

                                        if (field.type === "template") {
                                          // Template R: archive every
                                          // non-archived entry (both the
                                          // unsigned snapshot and any signed
                                          // versions) so the new cycle starts
                                          // clean. New Unsigned + Signed
                                          // entries are added at next Send
                                          // and final sign respectively.
                                          // Uses a functional update so we
                                          // archive the LATEST history map
                                          // (covers the case where signing
                                          // appended a Signed entry that
                                          // hasn't been reflected back into
                                          // the `history` closure yet).
                                          const archiveAt =
                                            new Date().toISOString();
                                          setHistory((prev) => {
                                            const list = prev[field.key] || [];
                                            if (list.length === 0) return prev;
                                            return {
                                              ...prev,
                                              [field.key]: list.map(
                                                (h: any) =>
                                                  h.status === "Archived"
                                                    ? h
                                                    : {
                                                        ...h,
                                                        status:
                                                          "Archived",
                                                        archivedAt:
                                                          h.archivedAt ||
                                                          archiveAt,
                                                      },
                                              ),
                                            };
                                          });
                                        } else if (
                                          !alreadyExists &&
                                          field.type !== "certificate" &&
                                          field.type !== "info"
                                        ) {
                                          setHistory({
                                            ...history,
                                            [field.key]: [
                                              ...existingHistory,
                                              {
                                                file: files[field.key],
                                                submittedBy: adminName,
                                                submittedAt:
                                                  new Date().toISOString(),
                                                status:
                                                  field.type === "info"
                                                    ? "Active"
                                                    : issued[field.key]
                                                      ? "Client Received"
                                                      : "Pending",
                                                date: new Date().toISOString(),
                                              },
                                            ],
                                          });
                                        }
                                        if (field.type === "certificate") {
                                          const existing =
                                            history[field.key] || [];

                                          if (existing.length === 0) {
                                            setHistory({
                                              ...history,
                                              [field.key]: [
                                                {
                                                  file: files[field.key],
                                                  submittedBy: userName,
                                                  submittedAt:
                                                    new Date().toISOString(),
                                                  status: "Client Submitted",
                                                },
                                              ],
                                            });
                                          } else {
                                            const updated = [...existing];
                                            updated[0] = {
                                              ...updated[0],
                                              status: "Active",
                                              date: new Date().toISOString(),
                                            };

                                            setHistory({
                                              ...history,
                                              [field.key]: updated,
                                            });
                                          }
                                        }
                                      }

                                      const updated = { ...files };
                                      delete updated[field.key];
                                      setFiles(updated);

                                      const updatedIssued = { ...issued };
                                      delete updatedIssued[field.key];
                                      setIssued(updatedIssued);

                                      // Clear read state so item reappears in GREEN after re-upload
                                      const nid = `${userId}_${field.key}`;
                                      saveNotifications(
                                        getNotifications().filter(
                                          (n) => n.id !== nid,
                                        ),
                                      );
                                    }}
                                  >
                                    R
                                  </button>
                                )}
                            </div>
                          ) : isUserView && field.type === "info" ? (
                            // Info doc the admin has uploaded but not yet
                            // issued (➤), or not uploaded at all. Nothing is
                            // available to this user yet — say so. Rendering an
                            // empty cell here made an unreleased document look
                            // like a dead link.
                            <div className="smrtdocs-upload-inner">Pending</div>
                          ) : (
                            ((isUserView && field.type === "certificate") ||
                              (field.type === "info" && !isUserView)) && (
                              <label className="smrtdocs-upload-inner">
                                Upload
                                <input
                                  type="file"
                                  style={{ display: "none" }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    const formData = new FormData();
                                    formData.append("file", file);

                                    try {
                                      const res = await fetch(
                                        `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/${encodeURIComponent(field.key)}?serviceIndex=${viewServiceIndex}`,
                                        {
                                          method: "POST",
                                          credentials: "include",
                                          body: formData,
                                        },
                                      );
                                      if (!res.ok)
                                        throw new Error("Upload failed");
                                      const ref = await res.json();

                                      // SAVE FILE
                                      setFiles({
                                        ...files,
                                        [field.key]: {
                                          name: ref.name,
                                          type: ref.type,
                                          blobName: ref.blobName,
                                          uploadedAt: ref.uploadedAt,
                                        },
                                      });

                                      // RESET FOR CERTIFICATE + TEMPLATE
                                      if (
                                        isUserView &&
                                        (field.type === "certificate" ||
                                          field.type === "template")
                                      ) {
                                        setIssued({
                                          ...issued,
                                          [field.key]: false,
                                        });
                                      }
                                    } catch (err) {
                                      console.error("File upload failed:", err);
                                    }
                                  }}
                                />
                              </label>
                            )
                          )}
                        </div>

                        <div className="smrtdocs-actions-cell">
                          {/* Download (user view). Navigational transfer: a real
                              <a href> to the backend attachment URL, so the
                              browser owns both the click and the save. An RBI /
                              managed browser suppresses synthetic clicks on
                              script-created anchors — a genuine click on a real
                              link is the one pattern that survives. No download
                              attribute (ignored cross-origin; the server's
                              Content-Disposition names the file) and no target
                              (an attachment response never unloads the page).
                              Shown against the same documents the user is
                              allowed to open. */}
                          {isUserView &&
                            files[field.key]?.blobName &&
                            (field.type === "template"
                              ? issued[field.key]?.status === "Active"
                              : !!issued[field.key]) && (
                              <a
                                className="btn-toggle smrtdocs-btn-download"
                                title="Download"
                                href={smrtDocsFileDownloadUrl(
                                  userId,
                                  files[field.key].blobName,
                                )}
                              >
                                <DownloadIcon />
                              </a>
                            )}

                          {isUserView &&
                            field.type === "template" &&
                            files[field.key] &&
                            issued[field.key]?.status !== "Active" &&
                            (() => {
                              // Order-enforced Sign trigger. Mirrors the gate
                              // previously rendered in the file area — only
                              // the active signer sees the button; others
                              // see the passive status label in that slot.
                              const it = issued[field.key];
                              const signers = Array.isArray(it?.signers)
                                ? it.signers
                                : [];
                              const idx =
                                typeof it?.currentSignerIndex === "number"
                                  ? it.currentSignerIndex
                                  : 0;
                              const currentSigner = signers[idx];
                              const isCurrent =
                                currentSigner &&
                                currentSigner.userId ===
                                  (authUser as any)?.userID;
                              const alreadySigned = signers.some(
                                (s: any) =>
                                  s.userId === (authUser as any)?.userID &&
                                  s.signedAt,
                              );
                              if (!isCurrent || alreadySigned) return null;
                              return (
                                <button
                                  className="btn-toggle smrtdocs-btn-issue"
                                  title="Sign"
                                  onClick={() =>
                                    setSigningFlow({
                                      fieldKey: field.key,
                                      documentName:
                                        files[field.key]?.name ||
                                        field.label ||
                                        "Document",
                                    })
                                  }
                                >
                                  <span
                                    style={{ fontSize: "14px", lineHeight: 1, padding: "0 4px" }}
                                  >
                                    Sign
                                  </span>
                                </button>
                              );
                            })()}
                          {!isUserView &&
                            field.type === "template" &&
                            files[field.key]?.blobName &&
                            !issued[field.key] && (
                              <button
                                className="btn-toggle smrtdocs-btn-multi"
                                title="Edit Fields"
                                onClick={() =>
                                  setSignModal({
                                    fieldKey: field.key,
                                    countersignRequired:
                                      !!effectiveField.countersignRequired,
                                    openPlacementAfter: true,
                                  })
                                }
                              >
                                <span
                                  style={{ fontSize: "22px", lineHeight: 1 }}
                                >
                                  ⊞
                                </span>
                              </button>
                            )}
                          {field.type === "template" &&
                            issued[field.key] &&
                            (issued[field.key].status === "Active" ||
                              issued[field.key].status === "Completed") &&
                            (() => {
                              // Admin always sees the certificate button.
                              // In user view, expose it to signers who
                              // actually signed this document so they can
                              // download their copy of the audit record.
                              if (!isUserView) return true;
                              const signers = Array.isArray(
                                issued[field.key]?.signers,
                              )
                                ? issued[field.key].signers
                                : [];
                              const myId = (authUser as any)?.userID;
                              return signers.some(
                                (s: any) =>
                                  s.userId === myId && s.signedAt,
                              );
                            })() && (
                              /* Real anchor: the certificate is generated
                                 server-side from stored state and served
                                 as an attachment — the download pattern
                                 that survives an RBI / managed browser. */
                              <a
                                className="btn-toggle"
                                title="Certificate of Completion"
                                href={smrtDocsCertificateUrl(
                                  userId,
                                  field.key,
                                  serviceId,
                                )}
                                style={{
                                  background: "#f3f4f6",
                                  borderColor: "#d1d5db",
                                  color: "#1f2937",
                                  textDecoration: "none",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "16px",
                                    lineHeight: 1,
                                  }}
                                >
                                  📜
                                </span>
                              </a>
                            )}
                          {!isUserView &&
                            canRevealEncrypted &&
                            field.type === "template" &&
                            issued[field.key] &&
                            (issued[field.key].status === "Active" ||
                              issued[field.key].status === "Completed") &&
                            (placements[field.key] || []).some(
                              (p: any) => p.type === "text-encrypted",
                            ) && (
                              <button
                                className="btn-toggle"
                                title="Reveal encrypted fields (download unmasked copy — logged)"
                                onClick={() => openUnmaskModal(field)}
                                style={{
                                  background: "#fef3c7",
                                  borderColor: "#f59e0b",
                                  color: "#1f2937",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "16px",
                                    lineHeight: 1,
                                  }}
                                >
                                  🔓
                                </span>
                              </button>
                            )}
                          {!isUserView &&
                            files[field.key] &&
                            (() => {
                              const hasReminder = !!reminders[field.key];
                              return (
                                <button
                                  className={`btn-toggle ${hasReminder ? "smrtdocs-btn-notify" : ""}`}
                                  style={
                                    !hasReminder
                                      ? {
                                          background: "#6b7280",
                                          borderColor: "#6b7280",
                                          opacity: 0.7,
                                        }
                                      : undefined
                                  }
                                  title="Set Expiry Reminder"
                                  onClick={() => {
                                    setNotifyOpen(
                                      field.key + "|" + field.label,
                                    );
                                  }}
                                >
                                  <span
                                    style={{ fontSize: "22px", lineHeight: 1 }}
                                  >
                                    ✉
                                  </span>
                                </button>
                              );
                            })()}
                          {!isUserView &&
                            field.type === "certificate" &&
                            effectiveField.approvalRequired &&
                            issued[field.key] &&
                            !approved[field.key] && (
                              <>
                                {/* APPROVE */}
                                <button
                                  className="btn-toggle btn-toggle-approval-on"
                                  title="Approve"
                                  onClick={() => {
                                    setApproved({
                                      ...approved,
                                      [field.key]: true,
                                    });

                                    const existing = history[field.key] || [];

                                    if (existing.length > 0) {
                                      const updated = [...existing];

                                      if (updated.length > 1) {
                                        updated[updated.length - 2] = {
                                          ...updated[updated.length - 2],
                                          status: "Archived",
                                          archivedAt: new Date().toISOString(),
                                        };
                                      }
                                      updated[updated.length - 1] = {
                                        ...updated[updated.length - 1],
                                        status: "Active",
                                        submittedBy:
                                          updated[updated.length - 1]
                                            .submittedBy || userName,
                                        submittedAt:
                                          updated[updated.length - 1]
                                            .submittedAt,
                                        approvedBy: adminName,
                                        approvedAt: new Date().toISOString(),
                                      };

                                      setHistory({
                                        ...history,
                                        [field.key]: updated,
                                      });
                                    }
                                  }}
                                >
                                  ✔
                                </button>

                                {/* REJECT */}
                                <button
                                  className="btn-toggle btn-toggle-delete"
                                  title="Reject"
                                  onClick={() => {
                                    const updatedFiles = { ...files };
                                    delete updatedFiles[field.key];
                                    setFiles(updatedFiles);

                                    const updatedIssued = { ...issued };
                                    delete updatedIssued[field.key];
                                    setIssued(updatedIssued);

                                    const updatedApproved = { ...approved };
                                    delete updatedApproved[field.key];
                                    setApproved(updatedApproved);

                                    const existing = history[field.key] || [];
                                    const updatedHistoryArray = [...existing];

                                    // remove only the latest (rejected submission)
                                    updatedHistoryArray.pop();

                                    setHistory({
                                      ...history,
                                      [field.key]: updatedHistoryArray,
                                    });
                                  }}
                                >
                                  D
                                </button>
                              </>
                            )}
                          {!isUserView &&
                            field.type === "template" &&
                            effectiveField.approvalRequired &&
                            issued[field.key] &&
                            issued[field.key].status === "Client Submitted" && (
                              <>
                                {/* APPROVE */}
                                <button
                                  className="btn-toggle btn-toggle-approval-on"
                                  title="Approve"
                                  onClick={() => {
                                    setApproved({
                                      ...approved,
                                      [field.key]: true,
                                    });

                                    const existing = history[field.key] || [];

                                    if (existing.length > 0) {
                                      const updated = [...existing];

                                      if (updated.length > 1) {
                                        updated[updated.length - 2] = {
                                          ...updated[updated.length - 2],
                                          status: "Archived",
                                          archivedAt: new Date().toISOString(),
                                        };
                                      }

                                      updated[updated.length - 1] = {
                                        ...updated[updated.length - 1],
                                        status: "Active",
                                        submittedBy:
                                          updated[updated.length - 1]
                                            .submittedBy || adminName,
                                        submittedAt:
                                          updated[updated.length - 1]
                                            .submittedAt ||
                                          new Date().toISOString(),
                                        approvedBy: adminName,
                                        approvedAt: new Date().toISOString(),
                                      };

                                      setHistory({
                                        ...history,
                                        [field.key]: updated,
                                      });
                                    }

                                    setIssued({
                                      ...issued,
                                      [field.key]: {
                                        ...issued[field.key],
                                        status: "Active",
                                      },
                                    });
                                  }}
                                >
                                  ✔
                                </button>

                                {/* REJECT */}
                                <button
                                  className="btn-toggle btn-toggle-delete"
                                  title="Reject"
                                  onClick={() => {
                                    const updatedFiles = { ...files };
                                    delete updatedFiles[field.key];
                                    setFiles(updatedFiles);

                                    const updatedIssued = { ...issued };
                                    delete updatedIssued[field.key];
                                    setIssued(updatedIssued);

                                    const updatedApproved = { ...approved };
                                    delete updatedApproved[field.key];
                                    setApproved(updatedApproved);

                                    setHistory({
                                      ...history,
                                      [field.key]: [],
                                    });
                                  }}
                                >
                                  D
                                </button>
                              </>
                            )}
                          {((field.type === "certificate" && isUserView) ||
                            (field.type === "info" && !isUserView)) &&
                            files[field.key] &&
                            !issued[field.key] && (
                              <button
                                className="btn-toggle smrtdocs-btn-issue"
                                title="Issue"
                                onClick={async () => {
                                  // TEMPLATE flow lives on the Edit Fields
                                  // button now (signers modal → placement
                                  // modal → commit). Keeping this guard so
                                  // the non-template paths below remain
                                  // untouched.
                                  if (field.type === "template") {
                                    return;
                                  }

                                  // EXISTING LOGIC (UNCHANGED)
                                  if (files[field.key]) {
                                    const existingHistory =
                                      history[field.key] || [];

                                    let updatedHistory = [...existingHistory];

                                    if (updatedHistory.length > 0) {
                                      updatedHistory = updatedHistory.map(
                                        (h: any) => ({
                                          ...h,
                                          status: "Archived",
                                          archivedAt: new Date().toISOString(),
                                        }),
                                      );
                                    }

                                    setHistory({
                                      ...history,
                                      [field.key]: [
                                        ...updatedHistory,
                                        {
                                          file: files[field.key],
                                          status:
                                            field.type === "info"
                                              ? "Active"
                                              : field.type === "certificate"
                                                ? effectiveField.approvalRequired
                                                  ? "Client Submitted"
                                                  : "Active"
                                                : "Client Received",
                                          submittedBy:
                                            field.type === "certificate"
                                              ? userName
                                              : adminName,
                                          submittedAt: new Date().toISOString(),
                                        },
                                      ],
                                    });
                                  }

                                  if (field.type === "certificate") {
                                    const updatedApproved = { ...approved };
                                    delete updatedApproved[field.key];
                                    setApproved(updatedApproved);
                                  }

                                  setIssued({
                                    ...issued,
                                    [field.key]: true,
                                  });
                                  setNotified({
                                    ...notified,
                                    [field.key]: false,
                                  });
                                }}
                              >
                                <span
                                  style={{ fontSize: "22px", lineHeight: 1 }}
                                >
                                  ➤
                                </span>
                              </button>
                            )}

                          {/* Notify button — shown after admin-initiated issue only */}
                          {!isUserView &&
                            (field.type === "info" ||
                              field.type === "template") &&
                            issued[field.key] &&
                            notified[field.key] === false && (
                              <button
                                className="btn-toggle smrtdocs-btn-issue"
                                style={{
                                  background: "#1faa59",
                                  borderColor: "#1faa59",
                                }}
                                title="Click to notify user of all newly issued documents"
                                onClick={() => setNotifyConfirmOpen(true)}
                              >
                                <span
                                  style={{ fontSize: "22px", lineHeight: 1 }}
                                >
                                  ✉
                                </span>
                              </button>
                            )}

                          {((history[field.key] &&
                            history[field.key].length > 0) ||
                            files[field.key]) && (
                            <button
                              className="btn-toggle smrtdocs-btn-history"
                              title="View History"
                              onClick={() => {
                                setHistoryOpen(
                                  field.key +
                                    "|" +
                                    field.label +
                                    "|" +
                                    (field.type || ""),
                                );
                              }}
                            >
                              ↻
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          })
        ) : (
          <div style={{ padding: "10px", color: "#999" }}>
            No documents issued
          </div>
        )}
      </div>
      {historyOpen &&
        (() => {
          const [key, label, type] = historyOpen.split("|");
          return (
            <SMRTDocsFileHistory
              isOpen={true}
              onClose={() => setHistoryOpen(null)}
              history={history[key] || []}
              title={label}
              isUserView={isUserView}
              documentType={type}
              onView={openFile}
              certificateUrl={
                type === "template" &&
                issued[key] &&
                (issued[key].status === "Active" ||
                  issued[key].status === "Completed")
                  ? smrtDocsCertificateUrl(userId, key, serviceId)
                  : undefined
              }
              onDelete={
                !isUserView
                  ? (index: number) => {
                      const updated = [...(history[key] || [])];
                      updated.splice(index, 1);

                      setHistory({
                        ...history,
                        [key]: updated,
                      });

                      // 🔥 HARD RESET IF EMPTY
                      if (updated.length === 0) {
                        const updatedFiles = { ...files };
                        delete updatedFiles[key];
                        setFiles(updatedFiles);

                        const updatedIssued = { ...issued };
                        delete updatedIssued[key];
                        setIssued(updatedIssued);

                        const updatedApproved = { ...approved };
                        delete updatedApproved[key];
                        setApproved(updatedApproved);
                      }
                    }
                  : undefined
              }
            />
          );
        })()}
      {notifyOpen &&
        (() => {
          const [key, label] = notifyOpen.split("|");

          return (
            <SMRTDocsNotifyModal
              isOpen={true}
              onClose={() => setNotifyOpen(null)}
              title={label}
              data={reminders[key]}
              onSave={(data: any) => {
                if (!data.reminders || data.reminders.length === 0) {
                  const updated = { ...reminders };
                  delete updated[key];
                  setReminders(updated);
                } else {
                  setReminders({
                    ...reminders,
                    [key]: {
                      ...data,
                      docLabel: label,
                      adminEmail: authUser?.email || "",
                      adminName: adminName,
                      userEmail: userEmail || "",
                      userName: userName || "",
                    },
                  });
                }
              }}
            />
          );
        })()}
      {signModal && (
        <SMRTDocsSignersModal
          isOpen={true}
          countersignRequired={signModal.countersignRequired}
          onClose={() => setSignModal(null)}
          onSubmit={async (signers: any[]) => {
            // Internal signing — persist the ordered signer roster on the
            // field's `issued` record. Status starts at "Client Received"
            // (no signers complete yet); existing transition logic at the
            // top of the row picks this up unchanged.
            // Hash the document content once at Send time so every signer
            // references the same fingerprint. Failures fall back to an
            // empty string (better than blocking the send).
            let documentHash = "";
            const fileObj = files[signModal.fieldKey];
            if (fileObj?.blobName) {
              try {
                const res = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/_?blobName=${encodeURIComponent(fileObj.blobName)}&format=base64`,
                  { credentials: "include" },
                );
                if (res.ok) {
                  const { base64 } = await res.json();
                  documentHash = await sha256OfBase64(base64);
                }
              } catch (err) {
                console.error("Failed to hash document at send:", err);
              }
            }
            if (signModal.openPlacementAfter) {
              // Edit Fields flow: park the picked signers + hash and
              // transition straight into the placement modal. Issued
              // status only flips when the placement modal saves so a
              // half-configured doc never enters the signing queue.
              setPendingSelection({
                fieldKey: signModal.fieldKey,
                signers,
                documentHash,
              });
              setPlacementModal({
                fieldKey: signModal.fieldKey,
                documentName:
                  files[signModal.fieldKey]?.name ||
                  "Document",
                blobName:
                  files[signModal.fieldKey]?.blobName || "",
                countersignRequired: signModal.countersignRequired,
              });
              setSignModal(null);
              return;
            }
            setIssued({
              ...issued,
              [signModal.fieldKey]: {
                signers: signers.map((s: any, i: number) => ({
                  userId: s.userId,
                  name: s.name,
                  email: s.email,
                  signingOrderPosition: i,
                  signedAt: null,
                })),
                currentSignerIndex: 0,
                status: "Client Received",
                name: files[signModal.fieldKey]?.name,
                documentHash,
              },
            });
            setNotified({ ...notified, [signModal.fieldKey]: false });
            setSignModal(null);
          }}
        />
      )}
      {unmaskModal && (
        <div
          className="smrtdocs-modal-overlay"
          style={{ zIndex: 10001 }}
          onClick={() => {
            if (!unmaskBusy) setUnmaskModal(null);
          }}
        >
          <div
            className="smrtdocs-modal"
            style={{ width: 460 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                Reveal Encrypted Fields
              </h2>
              <button
                className="btn-cancel"
                onClick={() => setUnmaskModal(null)}
                disabled={unmaskBusy}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body" style={{ padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  background: "rgba(245, 158, 11, 0.12)",
                  border: "1px solid #f59e0b",
                  borderRadius: 6,
                  padding: "10px 12px",
                  marginBottom: 14,
                }}
              >
                <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>🔓</span>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#e4e4e4",
                    lineHeight: 1.45,
                  }}
                >
                  This downloads a copy of{" "}
                  <strong>
                    {unmaskModal.field?.label || "this document"}
                  </strong>{" "}
                  with its encrypted field values shown in clear text. This
                  access is recorded in the audit log.
                </span>
              </div>

              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  color: "#9aa0a6",
                  marginBottom: 4,
                }}
              >
                Reason for access (required)
              </label>
              <input
                className="manage-input"
                style={{ width: "100%" }}
                placeholder="e.g. Verifying SIN for payroll onboarding"
                value={unmaskReason}
                onChange={(e) => setUnmaskReason(e.target.value)}
                disabled={unmaskBusy}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !unmaskBusy) runUnmaskDownload();
                }}
              />

              {unmaskError && (
                <div
                  style={{
                    color: "#ef9a9a",
                    background: "rgba(229, 57, 53, 0.12)",
                    padding: "8px 10px",
                    borderRadius: 4,
                    marginTop: 12,
                    fontSize: "0.85rem",
                  }}
                >
                  {unmaskError}
                </div>
              )}

              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <button
                  className="btn-cancel"
                  onClick={() => setUnmaskModal(null)}
                  disabled={unmaskBusy}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={runUnmaskDownload}
                  disabled={unmaskBusy || !unmaskReason.trim()}
                  style={{
                    opacity: unmaskBusy || !unmaskReason.trim() ? 0.6 : 1,
                    cursor:
                      unmaskBusy || !unmaskReason.trim()
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {unmaskBusy ? "Revealing…" : "Reveal & Download"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {placementModal && (
        <SMRTDocsFieldPlacementModal
          isOpen={true}
          documentBlobName={placementModal.blobName}
          documentName={placementModal.documentName}
          ownerUserId={userId}
          // Prefer the pending Edit Fields selection (signers picked
          // but not yet committed). Fall back to issued.signers for
          // legacy / re-issued docs, then positional names as a last
          // resort.
          signers={(() => {
            if (
              pendingSelection &&
              pendingSelection.fieldKey === placementModal.fieldKey &&
              Array.isArray(pendingSelection.signers) &&
              pendingSelection.signers.length > 0
            ) {
              return pendingSelection.signers.map((s: any) => ({
                userId: s.userId,
                name: s.name || s.email || s.userId,
              }));
            }
            const it = issued[placementModal.fieldKey];
            if (it && Array.isArray(it.signers) && it.signers.length > 0) {
              return it.signers.map((s: any) => ({
                userId: s.userId,
                name: s.name || s.email || s.userId,
              }));
            }
            const count = placementModal.countersignRequired ? 2 : 1;
            return Array.from({ length: count }, (_, i) => ({
              name: `Signer ${i + 1}`,
            }));
          })()}
          initialPlacements={placements[placementModal.fieldKey] || []}
          onClose={() => {
            setPlacementModal(null);
            setPendingSelection(null);
          }}
          onSave={(saved) => {
            setPlacements({
              ...placements,
              [placementModal.fieldKey]: saved,
            });
            // Edit Fields flow: now that placements are saved, commit
            // the buffered signer roster to `issued` and flip status to
            // "Client Received" so the first signer can begin.
            if (
              pendingSelection &&
              pendingSelection.fieldKey === placementModal.fieldKey
            ) {
              setIssued({
                ...issued,
                [placementModal.fieldKey]: {
                  signers: pendingSelection.signers.map(
                    (s: any, i: number) => ({
                      userId: s.userId,
                      name: s.name,
                      email: s.email,
                      signingOrderPosition: i,
                      signedAt: null,
                    }),
                  ),
                  currentSignerIndex: 0,
                  status: "Client Received",
                  name: files[placementModal.fieldKey]?.name,
                  documentHash: pendingSelection.documentHash,
                },
              });
              setNotified({
                ...notified,
                [placementModal.fieldKey]: false,
              });
              // Template flow: snapshot the unsigned document into history
              // at Send time. The signed counterpart is appended later when
              // the final signer submits. Both entries persist across the
              // cycle so the History tab shows the full pair.
              //
              // Defensive archive: archive any pre-existing non-archived
              // entries before appending. R should already have archived
              // them, but a state race (polling overwrite, stale closure)
              // can leave a prior cycle's "Active" entry visible alongside
              // the new cycle. Doing it again here guarantees the prior
              // cycle ends up Archived once the new cycle starts.
              const unsignedFile = files[placementModal.fieldKey];
              if (unsignedFile) {
                const sentAt = new Date().toISOString();
                setHistory((prev) => {
                  const list = prev[placementModal.fieldKey] || [];
                  const archived = list.map((h: any) =>
                    h.status === "Archived"
                      ? h
                      : {
                          ...h,
                          status: "Archived",
                          archivedAt: h.archivedAt || sentAt,
                        },
                  );
                  return {
                    ...prev,
                    [placementModal.fieldKey]: [
                      ...archived,
                      {
                        file: unsignedFile,
                        kind: "unsigned",
                        status: "Unsigned",
                        submittedBy: adminName,
                        submittedAt: sentAt,
                        date: sentAt,
                      },
                    ],
                  };
                });
              }
              setPendingSelection(null);
            }
            setPlacementModal(null);
          }}
        />
      )}
      {signingFlow && (
        <SMRTDocsSigningView
          isOpen={true}
          documentBlobName={files[signingFlow.fieldKey]?.blobName || ""}
          documentName={signingFlow.documentName}
          ownerUserId={userId}
          placements={placements[signingFlow.fieldKey] || []}
          mySignerIndex={(() => {
            const it = issued[signingFlow.fieldKey];
            return typeof it?.currentSignerIndex === "number"
              ? it.currentSignerIndex
              : 0;
          })()}
          mySignerName={(() => {
            const it = issued[signingFlow.fieldKey];
            const idx =
              typeof it?.currentSignerIndex === "number"
                ? it.currentSignerIndex
                : 0;
            return (it?.signers?.[idx]?.name as string) || "";
          })()}
          onClose={() => setSigningFlow(null)}
          onSubmit={async (result) => {
            const key = signingFlow.fieldKey;
            const current = issued[key];
            if (!current || !Array.isArray(current.signers)) {
              setSigningFlow(null);
              return;
            }
            const idx =
              typeof current.currentSignerIndex === "number"
                ? current.currentSignerIndex
                : 0;
            const signedAtIso = new Date().toISOString();
            // Per-signer verification code — deterministic SHA256 over
            // (documentHash + userId + signedAt). Stored full on the
            // signer + audit; the PDF embed uses the first 16 chars.
            const signerForCode = current.signers[idx] || {};
            const signerUserId =
              (signerForCode.userId as string) ||
              ((authUser as any)?.userID as string) ||
              "";
            const verificationCode = await sha256OfString(
              ((current.documentHash as string) || "") +
                signerUserId +
                signedAtIso,
            );
            // Per-placement values this signer just stamped. Signature
            // images go into `signatureImages`; initial / date / title
            // / text values go into `fieldValues` so the final PDF can
            // embed every captured field, not just signatures.
            const fieldPlacements = placements[key] || [];
            const mySigImages: Record<string, string> = {};
            const myFieldValues: Record<string, string> = {};
            for (const p of fieldPlacements) {
              if (p.signerIndex !== idx) continue;
              const v = result.fieldValues?.[p.id];
              if (typeof v !== "string" || !v) continue;
              if (p.type === "signature") {
                if (v.startsWith("data:image")) mySigImages[p.id] = v;
              } else {
                myFieldValues[p.id] = v;
              }
            }
            const updatedSigners = current.signers.map((s: any, i: number) =>
              i === idx
                ? {
                    ...s,
                    signedAt: signedAtIso,
                    signatureImages: {
                      ...(s.signatureImages || {}),
                      ...mySigImages,
                    },
                    fieldValues: {
                      ...(s.fieldValues || {}),
                      ...myFieldValues,
                    },
                    verificationCode,
                  }
                : s,
            );
            const allDone = idx + 1 >= updatedSigners.length;
            const nextIndex = allDone ? idx : idx + 1;
            // Audit record for this signer. Backend stamps ipAddress and
            // dedupes by (userId, signingOrderPosition). Append-only.
            const signerEntry = current.signers[idx] || {};
            const auditEntry = {
              userId:
                (signerEntry.userId as string) ||
                ((authUser as any)?.userID as string) ||
                "",
              orgId: (org?.orgID as string) || "",
              documentId: key,
              documentVersion:
                (files[key]?.uploadedAt as string) ||
                (files[key]?.blobName as string) ||
                "",
              documentHash: (current.documentHash as string) || "",
              signingOrderPosition:
                typeof signerEntry.signingOrderPosition === "number"
                  ? signerEntry.signingOrderPosition
                  : idx,
              signedAt: signedAtIso,
              ipAddress: "",
              userAgent:
                typeof navigator !== "undefined" ? navigator.userAgent : "",
              consentText:
                "By selecting Adopt and Sign, I agree that the signature and initials will be the electronic representation of my signature and initials for all purposes when I (or my agent) use them on documents, including legally binding contracts.",
              signatureType: result.signatureType,
              verificationCode,
            };
            setAudit({
              ...audit,
              [key]: [...(audit[key] || []), auditEntry],
            });
            // Once every signer has completed, flip status so existing
            // transition logic moves the row to "Active" or "Client
            // Submitted" per the Approval Required toggle. Audit / PDF
            // embedding land in the follow-up stage.
            setIssued({
              ...issued,
              [key]: {
                ...current,
                signers: updatedSigners,
                currentSignerIndex: nextIndex,
                status: allDone ? "Client Submitted" : "Client Received",
              },
            });
            if (allDone) {
              const now = new Date().toISOString();
              // Archive any pre-existing "Active" entries (legacy re-issue
              // flow). The unsigned snapshot recorded at Send time is left
              // intact; the signed entry is appended below once the signed
              // PDF has been uploaded so its `file` ref points at the
              // actual signed blob, not the unsigned source.
              setHistory((prev) => ({
                ...prev,
                [key]: (prev[key] || []).map((h: any) => ({
                  ...h,
                  status: h.status === "Active" ? "Archived" : h.status,
                  archivedAt: h.status === "Active" ? now : h.archivedAt,
                })),
              }));
              // Build the signed PDF and replace the File reference. The
              // original blob is preserved in storage (different blobName)
              // and on the issued record so it can be retrieved later.
              try {
                const origFile = files[key];
                if (origFile?.blobName) {
                  const baseRes = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/_?blobName=${encodeURIComponent(origFile.blobName)}&format=base64`,
                    { credentials: "include" },
                  );
                  if (baseRes.ok) {
                    const { base64 } = await baseRes.json();
                    const bin = atob(base64);
                    const origBytes = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++)
                      origBytes[i] = bin.charCodeAt(i);
                    const signedBytes = await buildSignedPdf({
                      originalBytes: origBytes,
                      placements: fieldPlacements,
                      signers: updatedSigners,
                    });
                    const blob = new Blob([signedBytes], {
                      type: "application/pdf",
                    });
                    const form = new FormData();
                    form.append(
                      "file",
                      blob,
                      (origFile.name || "document.pdf").replace(
                        /\.pdf$/i,
                        "_signed.pdf",
                      ),
                    );
                    const upRes = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(userId)}/${encodeURIComponent(key)}?serviceIndex=${viewServiceIndex}`,
                      {
                        method: "POST",
                        credentials: "include",
                        body: form,
                      },
                    );
                    if (upRes.ok) {
                      const ref = await upRes.json();
                      const signedFile = {
                        name: ref.name,
                        type: ref.type,
                        blobName: ref.blobName,
                        uploadedAt: ref.uploadedAt,
                      };
                      setFiles({
                        ...files,
                        [key]: signedFile,
                      });
                      // Template flow: append the signed snapshot to the
                      // history pair so both the unsigned (Send time) and
                      // signed (this moment) versions live side-by-side.
                      // Capture each signer's name + signedAt on the
                      // entry so the History expand can render a
                      // per-signer line without needing the live
                      // `issued` record (which gets cleared on R).
                      const signersSnapshot = updatedSigners.map(
                        (s: any) => ({
                          name: s?.name || s?.email || "",
                          signedAt: s?.signedAt || "",
                        }),
                      );
                      setHistory((prev) => ({
                        ...prev,
                        [key]: [
                          ...(prev[key] || []),
                          {
                            file: signedFile,
                            kind: "signed",
                            status: "Client Submitted",
                            submittedBy: userName,
                            submittedAt: now,
                            date: now,
                            signers: signersSnapshot,
                          },
                        ],
                      }));
                      // Track originalFile on the issued record so the
                      // unsigned source remains discoverable post-sign.
                      const finalIssued = {
                        ...current,
                        signers: updatedSigners,
                        currentSignerIndex: nextIndex,
                        status: "Client Submitted",
                        originalFile: origFile,
                      };
                      setIssued({ ...issued, [key]: finalIssued });
                    }
                  }
                }
              } catch (pdfErr) {
                console.error(
                  "Failed to build/upload signed PDF:",
                  pdfErr,
                );
              }
            }
            setSigningFlow(null);
          }}
        />
      )}
      {notifyConfirmOpen && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Send Notification</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p
                style={{
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                  margin: "0 0 20px",
                }}
              >
                If you plan to issue multiple documents, you may want to wait
                until all documents are issued before sending this notification.
                <br />
                <br />
                This will notify the user of all newly issued documents.
              </p>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  onClick={() => setNotifyConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={async () => {
                    // Collect all unnotified documents that are issued
                    const docNames: string[] = [];
                    const keysToMark: string[] = [];

                    const allSteps = [
                      ...(template?.steps || []),
                      ...(serviceLocalDocs.steps || []),
                    ];
                    for (const step of allSteps) {
                      const attached =
                        serviceLocalDocs.attachedFields?.[step.id] || [];
                      for (const field of [
                        ...(step.fields || []),
                        ...attached,
                      ]) {
                        if (notified[field.key] !== false) continue;
                        if (!issued[field.key]) continue;

                        // Check status is Client Received or Active
                        const issuedVal = issued[field.key];
                        let status = "";
                        if (
                          typeof issuedVal === "object" &&
                          issuedVal !== null
                        ) {
                          status = issuedVal.status || "";
                        } else if (issuedVal === true) {
                          status = "Active";
                        }

                        if (
                          status === "Client Received" ||
                          status === "Active"
                        ) {
                          docNames.push(field.label);
                          keysToMark.push(field.key);
                        }
                      }
                    }

                    console.log("SEND NOTIFICATION CLICKED", {
                      documentCount: docNames.length,
                    });

                    if (keysToMark.length > 0 && userEmail) {
                      const firstName = userName?.split(" ")[0] || "User";
                      const loginUrl = window.location.origin;
                      const emailBody = `Dear ${firstName},\n\n${appName} has issued the following document(s) requiring your attention:\n\n${docNames.map((n: string) => `• ${n}`).join("\n")}\n\nPlease log in to review.\n\n${loginUrl}`;

                      try {
                        const notifyRes = await fetch(
                          `${process.env.REACT_APP_BACKEND_URL}/notify/send`,
                          {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              to: userEmail,
                              subject: `Notice from ${appName}`,
                              body: emailBody,
                            }),
                          },
                        );
                        console.log("Notify response:", notifyRes.status);
                      } catch (err) {
                        console.error("Notify send error:", err);
                      }

                      const updatedNotified = { ...notified };
                      for (const key of keysToMark) {
                        updatedNotified[key] = true;
                      }
                      setNotified(updatedNotified);
                    }

                    setNotifyConfirmOpen(false);
                  }}
                >
                  Send Notification
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {localEditOpen && (
        <div className="smrtdocs-modal-overlay">
          <div
            className="smrtdocs-modal"
            style={{
              width: "900px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                Local Changes — This only affects this user
              </h2>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-save"
                  onClick={() => {
                    const allLocalFields: any[] = [];
                    for (const fields of Object.values(
                      serviceLocalDocs.attachedFields || {},
                    )) {
                      allLocalFields.push(...(fields as any[]));
                    }
                    for (const step of serviceLocalDocs.steps || []) {
                      allLocalFields.push(...(step.fields || []));
                    }
                    const hasInvalid = allLocalFields.some(
                      (f: any) => f.key?.startsWith("ldoc_") && !f.type,
                    );
                    if (hasInvalid) {
                      setLocalTypeError(true);
                      return;
                    }
                    setLocalEditOpen(false);
                  }}
                >
                  Save
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setLocalEditOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
            <div
              className="smrtdocs-modal-body"
              style={{ overflowY: "auto", flex: 1 }}
            >
              <SMRTDocsEditor
                template={{ steps: [] }}
                setTemplate={() => {}}
                lockedSteps={template?.steps || []}
                localTemplate={serviceLocalDocs}
                setLocalTemplate={setServiceLocalDocs}
              />
            </div>
            {localTypeError && (
              <div className="smrtdocs-modal-overlay" style={{ zIndex: 1001 }}>
                <div className="smrtdocs-modal" style={{ width: "420px" }}>
                  <div className="smrtdocs-modal-header">
                    <h2 style={{ margin: 0, fontSize: "1rem" }}>
                      Document Type Required
                    </h2>
                  </div>
                  <div className="smrtdocs-modal-body">
                    <p
                      style={{
                        fontSize: "0.9rem",
                        lineHeight: 1.5,
                        margin: "0 0 20px",
                      }}
                    >
                      You must choose a document type for all new documents
                      before saving.
                    </p>
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        className="btn-save"
                        onClick={() => setLocalTypeError(false)}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {fileError && (
        <div
          role="alert"
          onClick={() => setFileError(null)}
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10001,
            maxWidth: "min(560px, 90vw)",
            padding: "12px 16px",
            borderRadius: 8,
            background: "#fdecea",
            border: "1px solid #f5c2c0",
            color: "#8a1c13",
            fontSize: "0.9rem",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            cursor: "pointer",
          }}
        >
          {fileError}
        </div>
      )}
      {mobileViewFile && (
        <div className="smrtdocs-modal-overlay" style={{ zIndex: 10000 }}>
          <div
            className="smrtdocs-modal"
            style={{ width: "95vw", height: "85dvh", display: "flex", flexDirection: "column" }}
          >
            <div className="smrtdocs-modal-header">
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.95rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
                title={mobileViewFile.name}
              >
                {mobileViewFile.name}
              </h2>
              <button
                className="btn-cancel"
                title="Download"
                onClick={() =>
                  downloadBlobUrl(mobileViewFile.src, mobileViewFile.name)
                }
              >
                Download
              </button>
              <button className="btn-cancel" onClick={() => setMobileViewFile(null)}>
                Close
              </button>
            </div>
            {mobileViewFile.contentType.startsWith("image/") ? (
              <img
                src={mobileViewFile.src}
                alt={mobileViewFile.name}
                style={{ flex: 1, minHeight: 0, objectFit: "contain", background: "#000" }}
              />
            ) : (
              <iframe
                src={mobileViewFile.src}
                title={mobileViewFile.name}
                style={{ flex: 1, border: "none", borderRadius: "0 0 8px 8px", background: "#fff" }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
